import {
    defineStore,
} from 'pinia';
import * as minitaskService from '../services/minitask';
import {
    useProfileStore,
} from './profile-store';

function checkSkipStatus(skipDate) {
    if (!skipDate) return false;

    const skippedExpiredDate = new Date(skipDate);
    skippedExpiredDate.setDate(skippedExpiredDate.getDate() + 14);

    return skippedExpiredDate > new Date();
}

/**
 * @typedef {'birthdate', 'image', 'address', 'desiredJobEmploymentType', 'desiredJobJobfield',
 * 'desiredJobTravelReadiness', 'desiredJobWorkFromHome', 'language', 'mobile', 'work', 'jobTitle', 'jobField',
 * 'visibility', 'softSkills'} minitasks
 */

export const useMinitaskStore = defineStore('minitasks', {
    state: () => ({
        meta: {
            uuid: null,
            hideSuccess: null,
            showSuccess: null,
            skipped: null,
        },
        data: {
            birthdate: null,
            mobile: null,
            image: null,
            address: null,
            desiredJobEmploymentType: null,
            desiredJobJobfield: null,
            desiredJobTravelReadiness: null,
            desiredJobWorkFromHome: null,
            language: null,
            work: null,
            softSkills: null,
            jobTitle: null,
            jobField: null,
            visibility: null,
        },
    }),
    getters: {
        addressDisplayStatus(state) {
            const profile = useProfileStore();
            return profile.basics.town && !profile.basics.street
                && !checkSkipStatus(state.data.address);
        },
        birthdateDisplayStatus(state) {
            const profile = useProfileStore();
            return !profile.basics.birthdate && !checkSkipStatus(state.data.birthdate);
        },
        imageDisplayStatus(state) {
            const profile = useProfileStore();
            return !profile.image[0] && !checkSkipStatus(state.data.image);
        },
        desiredJobEmploymentTypeDisplayStatus(state) {
            const profile = useProfileStore();
            return !profile.desiredJob.employment[0] && !checkSkipStatus(state.data.desiredJobEmploymentType);
        },
        desiredJobJobfieldDisplayStatus(state) {
            const profile = useProfileStore();
            return !profile.desiredJob.jobFields[0] && !checkSkipStatus(state.data.desiredJobJobfield);
        },
        desiredJobTravelReadinessDisplayStatus(state) {
            const profile = useProfileStore();
            return (profile.desiredJob.travelReadiness.value === null)
                && !checkSkipStatus(state.data.desiredJobTravelReadiness);
        },
        desiredJobWorkFromHomeDisplayStatus(state) {
            const profile = useProfileStore();
            return (profile.desiredJob.workFromHome.value === null)
                && !checkSkipStatus(state.data.desiredJobWorkFromHome);
        },
        languageDisplayStatus(state) {
            const profile = useProfileStore();
            return !profile.languages.languages[0] && !checkSkipStatus(state.data.language);
        },
        telephoneDisplayStatus(state) {
            const profile = useProfileStore();
            return !profile.basics.mobile && !checkSkipStatus(state.data.mobile);
        },
        workDisplayStatus(state) {
            const profile = useProfileStore();
            return !profile.workExperiences.workExperiences[1] && !checkSkipStatus(state.data.work);
        },
        softSkillsSkippedStatus(state) {
            return !checkSkipStatus(state.data.softSkills);
        },
        jobTitleDisplayStatus(state) {
            return !checkSkipStatus(state.data.jobTitle);
        },
        jobFieldDisplayStatus(state) {
            return !checkSkipStatus(state.data.jobField);
        },
        visibilityDisplayStatus(state) {
            const profile = useProfileStore();
            return !profile.profileIsVisible && !checkSkipStatus(state.data.visibility)
                && profile.completeness >= 50;
        },
        successDisplayStatus(state) {
            return !state.meta.hideSuccess && state.meta.showSuccess;
        },
    },
    actions: {
        fetchMinitasks(data) {
            const minitasks = minitaskService.getMinitasks(data.uuid);
            if (minitasks) {
                this.set(minitasks);
            }
            data.callback();
        },
        setMinitaskSuccessStatus() {
            this.meta.showSuccess = true;
        },
        setMinitaskSuccessHide() {
            this.meta.hideSuccess = true;
        },
        /**
         * @param {minitasks} minitask
         */
        setSkipped(minitask) {
            minitaskService.setMinitaskSkipped(minitask, new Date());
            this.data[minitask] = new Date();
        },
        set(minitasks) {
            this.$patch(minitasks);
        },
    },
});
